import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useScrollPagination } from 'hooks/utility';
import { useTypedDispatch, useTypedSelector } from 'store';
import { useFilters } from 'store/slices/filters/hooks/useFilters';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { bnFrom, Bus } from 'tools';
import { IDexBuySellBotWallet } from 'types/bots';
import { EDexPairFilters } from 'types/filters';
import { IConnectWalletBalanceTaskRow } from 'types/tables/dex/connectWalletBalanceTaskRow';
import { formatFiat } from 'utils/formats/formatFiat';
import { formatToken } from 'utils/formats/formatToken';
import { EExchange } from 'web3';

import { useDexBuyAndSellWallets } from '../api';

type DexBuyAndSellWalletsRecordsProps = {
  task_id: number;
  is_connected: boolean;
};

const useDexBuyAndSellWalletsRecords = ({
  task_id,
  is_connected,
}: DexBuyAndSellWalletsRecordsProps) => {
  const dispatch = useTypedDispatch();
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;
  const { settedPairFilters } = useFilters({
    dex: {
      pair: dexPair,
      type: EDexPairFilters.buyAndSellWallets,
    },
  });

  const [virtualRecords, setVirtualRecords] = useState<IDexBuySellBotWallet[]>([]);

  const getWallets = useDexBuyAndSellWallets();

  const getRecords = useCallback(
    async ({
      limit,
      lastSeenId,
    }: {
      limit: number;
      lastSeenId: number;
    }): Promise<{ records: IDexBuySellBotWallet[]; has_more: boolean } | undefined> => {
      try {
        const { isSuccess, errorMessage, data } = await getWallets({
          lastSeenId,
          limit,
          filters: settedPairFilters,
          task_id,
          is_connected,
        });

        if (isSuccess && data) {
          return {
            records: data.items,
            has_more: data.has_next,
          };
        } else {
          dispatch(
            setAlertState({
              type: 'failed',
              text: errorMessage ?? 'Something went wrong',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => dispatch(dropAlertState()),
            }),
          );
          return undefined;
        }
      } catch (error) {
        console.log(error);
        return undefined;
      }
    },
    [dispatch, getWallets, settedPairFilters, task_id, is_connected],
  );

  const {
    hasMore,
    loading,
    loadMore,
    remount,
    records,
    setRecords,
    rowSelection,
    setRowSelection,
    selectedRecords,
  } = useScrollPagination({
    limit: 200,
    getRecords,
    remountLoad: true,
  });

  useUpdateEffect(() => {
    remount();
  }, [is_connected]);

  useEffect(() => {
    const onFilter = () => {
      remount();
    };

    Bus.on(`${EExchange.dex}:${EDexPairFilters.buyAndSellWallets}:FILTER`, onFilter);

    return () => {
      Bus.off(`${EExchange.dex}:${EDexPairFilters.buyAndSellWallets}:FILTER`, onFilter);
    };
  }, [remount]);

  const tableRecords = useMemo(
    () =>
      records?.map(record => {
        const addressCol = { address: record.address };

        const baseToken = record.tokens.find(item => item.token.id === dexPair.token_base.id)!;
        const tokenBaseCol = {
          value: formatToken(bnFrom(baseToken?.balance), baseToken?.token.decimals),
        };

        const quoteToken = record.tokens.find(item => item.token.id === dexPair.token_quote.id)!;
        const tokenQuoteCol = {
          value: formatToken(bnFrom(quoteToken?.balance), quoteToken?.token.decimals),
        };

        const feeToken = record.tokens.find(item => item.token.id === dexPair.token_fee.id)!;
        const tokenFeeCol = {
          value: formatToken(bnFrom(feeToken?.balance), feeToken?.token.decimals),
        };

        const totalToken = record.tokens.find((item: any) => item.token.symbol === 'TOTAL');
        const totalCol = { value: formatFiat(bnFrom(totalToken?.balance_usd), 6) };

        return {
          addressCol,
          tokenBaseCol,
          tokenQuoteCol,
          tokenFeeCol,
          totalCol,
          id: record.id,
        } as IConnectWalletBalanceTaskRow;
      }) ?? [],
    [records, dexPair],
  );

  const result = useMemo(
    () => ({
      records: { get: records, set: setRecords },
      virtualRecords: { get: virtualRecords, set: setVirtualRecords },
      tableRecords,
      hasMore,
      loading,
      loadMore,
      rowSelection: { get: rowSelection, set: setRowSelection },
      selectedRecords,
    }),
    [
      records,
      setRecords,
      hasMore,
      loading,
      loadMore,
      selectedRecords,
      rowSelection,
      setRowSelection,
      virtualRecords,
      setVirtualRecords,
      tableRecords,
    ],
  );

  return result;
};

export { useDexBuyAndSellWalletsRecords };
